import { useMemo, useState } from "react";
import { Check, Copy, Plus, Search, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/context";
import { cn } from "@/lib/utils";
import type { PlaygroundSession } from "../types";

function CopyButton({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
          })
          .catch(() => {
            // clipboard may be blocked — best-effort
          });
      }}
      title={label}
      aria-label={label}
      className="inline-flex items-center text-[var(--pg-text-3)] transition-colors hover:text-[var(--pg-text-2)]"
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  );
}

export { CopyButton };

interface Props {
  sessions: Record<string, PlaygroundSession>;
  sessionIndex: string[];
  activeSessionId: string | null;
  onCreate(): void;
  onSelect(id: string): void;
  onDelete(id: string): void;
}

/**
 * Sessions panel (264px).
 *
 * Search bar + new-session CTA + grouped list (TODAY / YESTERDAY / EARLIER).
 * The selected card carries the session id mono row for procurement
 * scannability. PII / DENY pills tag flagged sessions; DENY wins.
 */
export function PlaygroundSessionsPanel({
  sessions,
  sessionIndex,
  activeSessionId,
  onCreate,
  onSelect,
  onDelete,
}: Props) {
  const [query, setQuery] = useState("");
  const { t } = useTranslation();

  const buckets = useMemo(() => {
    const today: PlaygroundSession[] = [];
    const yesterday: PlaygroundSession[] = [];
    const earlier: PlaygroundSession[] = [];
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86_400_000;
    const needle = query.trim().toLowerCase();
    for (const id of sessionIndex) {
      const s = sessions[id];
      if (!s) continue;
      if (needle && !s.title.toLowerCase().includes(needle) && !s.id.includes(needle))
        continue;
      const t = new Date(s.updatedAt).getTime();
      if (t >= startOfToday) today.push(s);
      else if (t >= startOfYesterday) yesterday.push(s);
      else earlier.push(s);
    }
    return { today, yesterday, earlier };
  }, [sessions, sessionIndex, query]);

  return (
    <aside
      className="flex h-full w-[264px] flex-col border-r"
      style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
    >
      <header className="flex items-center justify-between px-4 pb-3 pt-4">
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-medium tracking-tight">
            {t("playground.sessions.title")}
          </h2>
          <span className="mono text-xs" style={{ color: "var(--pg-text-3)" }}>
            [{sessionIndex.length}]
          </span>
        </div>
        <Button
          variant="default"
          size="icon"
          onClick={onCreate}
          aria-label={t("playground.sessions.newButtonLabel")}
          title={t("playground.sessions.newButtonLabel")}
          className="h-[26px] w-[26px]"
        >
          <Plus size={14} />
        </Button>
      </header>

      <div className="px-3 pb-3">
        <label className="relative block">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            style={{ color: "var(--pg-text-3)" }}
          />
          <input
            type="search"
            placeholder={t("playground.sessions.searchPlaceholder")}
            aria-label={t("playground.sessions.searchAriaLabel")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="block w-full rounded-md border bg-transparent py-1.5 pl-7 pr-12 text-xs outline-none transition-colors"
            style={{
              borderColor: "var(--pg-border)",
              color: "var(--pg-text)",
            }}
          />
          <span
            aria-hidden="true"
            className="mono pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs"
            style={{ color: "var(--pg-text-3)" }}
          >
            ⌘K
          </span>
        </label>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-3">
        <SessionsGroup
          label={t("playground.sessions.buckets.today")}
          sessions={buckets.today}
          activeSessionId={activeSessionId}
          onSelect={onSelect}
          onDelete={onDelete}
        />
        <SessionsGroup
          label={t("playground.sessions.buckets.yesterday")}
          sessions={buckets.yesterday}
          activeSessionId={activeSessionId}
          onSelect={onSelect}
          onDelete={onDelete}
        />
        <SessionsGroup
          label={t("playground.sessions.buckets.earlier")}
          sessions={buckets.earlier}
          activeSessionId={activeSessionId}
          onSelect={onSelect}
          onDelete={onDelete}
        />
        {sessionIndex.length === 0 && (
          <div
            className="mt-4 rounded-lg border px-3 py-4 text-xs"
            style={{
              borderColor: "var(--pg-border)",
              background: "var(--pg-bg)",
              color: "var(--pg-text-2)",
            }}
          >
            <p>{t("playground.sessions.empty.title")}</p>
            <Button
              variant="default"
              size="sm"
              onClick={onCreate}
              className="mt-2"
            >
              {t("playground.sessions.empty.cta")}
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
}

function SessionsGroup({
  label,
  sessions,
  activeSessionId,
  onSelect,
  onDelete,
}: {
  label: string;
  sessions: PlaygroundSession[];
  activeSessionId: string | null;
  onSelect(id: string): void;
  onDelete(id: string): void;
}) {
  if (sessions.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="eyebrow px-2 pb-1.5">{label}</div>
      <ul className="space-y-1">
        {sessions.map((s) => (
          <SessionRow
            key={s.id}
            session={s}
            active={s.id === activeSessionId}
            onSelect={() => onSelect(s.id)}
            onDelete={() => onDelete(s.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function SessionRow({
  session,
  active,
  onSelect,
  onDelete,
}: {
  session: PlaygroundSession;
  active: boolean;
  onSelect(): void;
  onDelete(): void;
}) {
  const { t } = useTranslation();
  const piiCount = session.messages.reduce(
    (n, m) => n + (m.redactedTypes?.length ?? 0),
    0
  );
  const deny = session.messages.some(
    (m) => m.error || m.flagged === "policy"
  );
  const relative = formatRelative(session.updatedAt, t);

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? "true" : undefined}
        className={cn(
          "group block w-full rounded-lg border px-3 py-2 text-left transition-colors",
          active ? "" : "hover:bg-[var(--pg-bg)]"
        )}
        style={{
          borderColor: active ? "var(--pg-blue-ring)" : "transparent",
          background: active ? "var(--pg-blue-soft)" : "transparent",
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div
            className="truncate text-sm font-medium"
            style={{ color: "var(--pg-text)" }}
          >
            {session.title}
          </div>
          {deny ? (
            <span className="pill pill--danger" aria-label={t("playground.sessions.pills.deny")}>
              {t("playground.sessions.pills.deny")}
            </span>
          ) : piiCount > 0 ? (
            <span className="pill pill--warn" aria-label={t("playground.sessions.pills.pii")}>
              {t("playground.sessions.pills.pii")}
            </span>
          ) : null}
        </div>
        <div
          className="mono mt-1 truncate text-xs"
          style={{ color: "var(--pg-text-3)" }}
        >
          {session.model ?? t("playground.chat.meta.modelPickPlaceholder")} · {session.messages.length} msgs · {relative}
        </div>
        {active && (
          <div
            className="mono mt-1.5 flex items-center justify-between border-t border-dashed pt-1 text-xs"
            style={{ borderColor: "var(--pg-blue-ring)", color: "var(--pg-text-3)" }}
          >
            <span className="flex items-center gap-1.5">
              <span title={session.id}>session:{session.id.slice(0, 12)}…</span>
              <CopyButton value={session.id} label={t("playground.sessions.copyIdLabel")} />
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(t("playground.sessions.confirmDelete"))) onDelete();
              }}
              className="ml-2 inline-flex items-center text-[var(--pg-text-3)] transition-colors hover:text-[var(--pg-danger)]"
              aria-label={t("playground.sessions.deleteAriaLabel")}
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </button>
    </li>
  );
}

type Translate = ReturnType<typeof useTranslation>["t"];

function formatRelative(iso: string, t: Translate): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 60_000) return t("playground.sessions.time.justNow");
  if (diff < 3_600_000)
    return t("playground.sessions.time.minutesAgo", { n: Math.floor(diff / 60_000) });
  if (diff < 86_400_000)
    return t("playground.sessions.time.hoursAgo", { n: Math.floor(diff / 3_600_000) });
  const days = Math.floor(diff / 86_400_000);
  return t("playground.sessions.time.daysAgo", { n: days });
}
